import { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../api';
import { formatCurrency } from '../utils/currency';

import { useMsal } from '@azure/msal-react';
import html2pdf from 'html2pdf.js';
import {
  CalendarIcon,
  TagIcon,
  BuildingIcon,
  MapPinIcon,
  LayersIcon,
  UserIcon,
  ArrowLeft,
  FileDownIcon,
  UserPlus,
  Send,
  Pencil
} from 'lucide-react';
import {
  BsFileEarmarkText,
  BsBuilding,
  BsGeoAlt,
  BsBoxSeam,
  BsCalendarDate,
} from 'react-icons/bs';
import { useNavigate, useLocation } from 'react-router-dom';

function ExpenseReport() {
  const { expenseId: paramExpenseId } = useParams();
  const [expense, setExpense] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lineItems, setLineItems] = useState([]);
  const [derivedApproved, setDerivedApproved] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editedVendor, setEditedVendor] = useState({});
  const [showEditDetailsModal, setShowEditDetailsModal] = useState(false);
  const [editDetails, setEditDetails] = useState({});

  const categoryOptions = {
    IT: {
      Inventory: ['PRINTER', 'CARTRIDGE REFILING', 'MONITOR', 'MOUSE', 'KEYBOARD', 'LAPTOP', 'DESKTOP', 'MISCELLANEOUS ITEMS'],
      'Non-Inventory': ['SOFTWARE LICENSES','MICROSOFT AZURE GB','MICROSOFT AZURE NA','LINNWORKS','ECODEL DATAWAREHOUSE CONNECTOR', 'OFFICE LICENSES 365', 'MICROSOFT AZURE (VMs)','ADOBE LICENSES', 'OTHERS', 'BUSINESS CENTRAL LICENSES', 'TASKLET LICENSES'],
    },
    Admin: {
      Inventory: ['HOUSEKEEPING MATERIALS', 'STATIONARY MATERIALS', 'TEA & COFFEE', 'OTHERS', 'IT', 'MOBILE & CUG'],
      'Non-Inventory': ['COURIER', 'CUG BILLS', 'ELECTRICITY', 'EVENTS', 'FOOD', 'FUEL', 'FURNITURE', 'GARDEN SERVICE','GENERAL MAINTAINENCE', 'HOUSEKEEPING', 'PAYROLL', 'PRINTING', 'SECURITY SERVICE', 'WATER', 'OTHERS', 'HOSPITALITY -INDIA', 'HOSPITALITY -GLOBAL', 'GLOBAL'],
    },
    HR: {
      Inventory: ['GIFT PACKS', 'HR KIT'],
      'Non-Inventory': ['TRAINING', 'EVENTS'],
    },
    Others: {
      Inventory: ['GENERIC'],
      'Non-Inventory': ['MISCELLANEOUS'],
    },
    Accounts: {
      Inventory: ['ACCOUNTING FEES', 'BANK CHARGES', 'TAX CONSULTANCY', 'AUDIT FEES', 'LEGAL FEES', 'OTHERS'],
      'Non-Inventory': ['ACCOUNTING FEES', 'BANK CHARGES', 'TAX CONSULTANCY', 'AUDIT FEES', 'LEGAL FEES', 'OTHERS'],
    },
  };
  const departments = ['IT', 'HR', 'Admin', 'Accounts', 'Others'];
  const regions = ['India', 'UK', 'USA', 'Poland'];
  const groups = ['Inventory', 'Non-Inventory'];
  const { instance, accounts } = useMsal();
  const activeAccount = accounts[0];
  const pdfRef = useRef();
  const navigate = useNavigate();
  const location = useLocation();

  const decodeURIComponentSafe = (val) => {
    try { return decodeURIComponent(val); } catch { return val; }
  };

  // Support wildcard route: extract ID from path if not provided as param
  const routeExpenseId = (() => {
    const prefix = '/expense-report/';
    const idx = location.pathname.indexOf(prefix);
    if (idx === -1) return '';
    return location.pathname.slice(idx + prefix.length);
  })();

  const effectiveId = paramExpenseId || routeExpenseId || '';

  useEffect(() => {
    if (!effectiveId) {
      console.error('No expenseId provided');
      setLoading(false);
      return;
    }

    const fetchExpenseData = async () => {
      setLoading(true);
      try {
        // First decode the ID in case it was encoded once or more
        let decodedId = effectiveId;
        try { decodedId = decodeURIComponent(decodedId); } catch (e) {
          console.warn('First decode attempt failed:', e);
        }
        try { decodedId = decodeURIComponent(decodedId); } catch (e) {
          console.warn('Second decode attempt failed:', e);
        }
        // Extra normalization: collapse encoded slashes to literal slashes
        let normalizedId = String(decodedId).replace(/%252F/gi, '%2F').replace(/%2F/gi, '/');
        const enc1 = encodeURIComponent(normalizedId);
        const enc2 = encodeURIComponent(enc1);
        const encoded = import.meta.env.PROD ? enc2 : enc1;
        
        // Fetch data for expense
        
        // Keep original path-based API structure; in production use double-encoded to survive a proxy decode-once
        const [expenseRes, vendorRes, itemsRes] = await Promise.allSettled([
          api.get(`/api/expenses/${encoded}`),
          api.get(`/api/expenses/${encoded}/vendors`),
          api.get(`/api/expenses/${encoded}/items`)
        ]);

        // Check if the main expense data was successfully fetched
        if (expenseRes.status === 'rejected') {
          console.error('Failed to fetch expense data:', expenseRes.reason);
          throw new Error(`Failed to load expense data: ${expenseRes.reason.message}`);
        }

        // Process responses
        const expenseData = expenseRes.value.data;
        let vendorsData = vendorRes.status === 'fulfilled' ? vendorRes.value.data : [];
        let itemsData = itemsRes.status === 'fulfilled' ? itemsRes.value.data : [];

        // Fallback for production: if path-based fetch returns empty, refetch via query using the DB's expense_id
        if (import.meta.env.PROD) {
          try {
            if (!Array.isArray(vendorsData) || vendorsData.length === 0) {
              const vAlt = await api.get('/api/expenses/vendors/by', {
                params: { expenseId: expenseData.expense_id }
              });
              vendorsData = Array.isArray(vAlt.data) ? vAlt.data : vendorsData;
            }
          } catch (e) {
            console.warn('Vendors query fallback failed:', e);
          }
          try {
            if (!Array.isArray(itemsData) || itemsData.length === 0) {
              const iAlt = await api.get('/api/expenses/items/by-id', {
                params: { expenseId: expenseData.expense_id }
              });
              itemsData = Array.isArray(iAlt.data) ? iAlt.data : itemsData;
            }
          } catch (e) {
            console.warn('Items query fallback failed:', e);
          }
        }
        
        // Log any failed API calls
        if (vendorRes.status === 'rejected') {
          console.warn('Failed to fetch vendors, using empty array:', vendorRes.reason);
        }
        if (itemsRes.status === 'rejected') {
          console.warn('Failed to fetch items, using empty array:', itemsRes.reason);
        }
        
        setExpense(expenseData);
        setVendors(vendorsData);
        setLineItems(itemsData);
        try {
          const hRes = await api.get('/api/approvals/history/by', { params: { expenseId: expenseData.expense_id } });
          const history = Array.isArray(hRes.data) ? hRes.data : [];
          const l3 = history.some(h => Number(h.level) === 3 && String(h.status || '').trim().toLowerCase() === 'approved');
          setDerivedApproved(l3);
        } catch (e) {
          setDerivedApproved(false);
        }
        
      } catch (err) {
        console.error('Error in fetchExpenseData:', {
          name: err.name,
          message: err.message,
          stack: err.stack,
          response: err.response ? {
            status: err.response.status,
            statusText: err.response.statusText,
            data: err.response.data
          } : 'No response',
          config: err.config ? {
            url: err.config.url,
            method: err.config.method,
            headers: err.config.headers
          } : 'No config'
        });
        
        // Show user-friendly error message
        alert(`Failed to load expense data: ${err.message || 'Unknown error'}`);
        
        // Optionally redirect or handle the error state
        if (err.response?.status === 401) {
          // Handle unauthorized (e.g., redirect to login)
          window.location.href = '/';
        }
      } finally {
        setLoading(false);
      }
    };

    fetchExpenseData();
  }, [effectiveId]);

  const handleExportPDF = () => {
    const element = pdfRef.current;
    const options = {
      margin: 0.5,
      filename: `Expense_Report_${expense.expense_id}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' }
    };
    html2pdf().set(options).from(element).save();
  };

  const handleSubmitForApproval = async () => {
    try {
      if (!activeAccount) {
        alert('User not authenticated. Please log in again.');
        return;
      }

      const tokenResponse = await instance.acquireTokenSilent({
        scopes: ['Mail.Send', 'User.Read'],
        account: activeAccount
      });

      // Normalize and encode expenseId similar to fetch logic
      let d1;
      try { d1 = decodeURIComponent(expense.expense_id); } catch { d1 = expense.expense_id; }
      let d2;
      try { d2 = decodeURIComponent(d1); } catch { d2 = d1; }
      const norm = String(d2).replace(/%252F/gi, '%2F').replace(/%2F/gi, '/');
      const enc1 = encodeURIComponent(norm);
      const enc2 = encodeURIComponent(enc1);
      const preferred = import.meta.env.PROD ? enc2 : enc1;

      const payload = {
        creatorEmail: activeAccount.username,
        accessToken: tokenResponse.accessToken
      };

      // Try path with preferred encoding, then single-encoded, then body-based fallback(s)
      let submitted = false;
      let lastError = null;
      try {
        await api.post(`/api/expenses/${preferred}/submit`, payload);
        submitted = true;
      } catch (e1) {
        lastError = e1;
        if (e1?.response?.status === 404) {
          try {
            await api.post(`/api/expenses/${enc1}/submit`, payload);
            submitted = true;
          } catch (e2) {
            lastError = e2;
            // Final fallbacks: body/query based endpoints
            const body1 = { ...payload, expenseId: expense.expense_id };
            const body2 = { ...payload, expense_id: expense.expense_id };
            try {
              await api.post(`/api/expenses/submit`, body1);
              submitted = true;
            } catch (e3) {
              lastError = e3;
              try {
                await api.post(`/api/expenses/submit`, body2);
                submitted = true;
              } catch (e4) {
                lastError = e4;
                try {
                  await api.post(`/api/expenses/submit?expenseId=${encodeURIComponent(expense.expense_id)}`, payload);
                  submitted = true;
                } catch (e5) {
                  lastError = e5;
                  try {
                    await api.post(`/api/approvals/submit`, body1);
                    submitted = true;
                  } catch (e6) {
                    lastError = e6;
                  }
                }
              }
            }
          }
        }
      }

      if (!submitted) {
        console.error('Submit fallbacks exhausted. Last error:', {
          message: lastError?.message,
          status: lastError?.response?.status,
          data: lastError?.response?.data,
          url: lastError?.config?.url
        });
        throw lastError || new Error('Submit failed');
      }

      alert('Submitted for approval');
      setExpense({ ...expense, status: 'Pending Level 1' });
    } catch (err) {
      console.error('Error submitting for approval:', err);
      const serverMsg = err?.response?.data?.error || err?.response?.data?.message || (typeof err?.response?.data === 'string' ? err.response.data : '');
      const reason = serverMsg || err?.message || 'Unknown error';
      alert(`Submission failed: ${reason}`);
    }
  };

  const handleEditClick = (vendor) => {
    setEditingVendor({ ...vendor });
    setShowEditModal(true);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditingVendor((prev) => ({ ...prev, [name]: value }));
  };

  const handleDeleteVendor = async (vendorId) => {
    if (!window.confirm('Are you sure you want to delete this vendor?')) return;

    try {
      await api.delete(`/api/expenses/vendors/${vendorId}`);
      const d1 = decodeURIComponentSafe(effectiveId);
      const norm = String(d1).replace(/%252F/gi, '%2F').replace(/%2F/gi, '/');
      const enc1 = encodeURIComponent(norm);
      const enc2 = encodeURIComponent(enc1);
      const encoded = import.meta.env.PROD ? enc2 : enc1;
      const updated = await api.get(`/api/expenses/${encoded}/vendors`);
      setVendors(updated.data);
    } catch (err) {
      console.error('Failed to delete vendor:', err);
      alert('Deletion failed');
    }
  };

  const handleEditSave = async () => {
    try {
      const result = await instance.acquireTokenSilent({
        scopes: ["https://graph.microsoft.com/.default"],
        account: accounts[0],
      });

      const accessToken = result.accessToken;

      const formData = new FormData();
      formData.append('vendor_name', editingVendor.vendor_name);
      formData.append('cost', editingVendor.cost);
      formData.append('payment_terms', editingVendor.payment_terms);
      formData.append('description', editingVendor.description || '');

      if (editingVendor.newFile) {
        formData.append('file', editingVendor.newFile);
        formData.append('accessToken', accessToken);
      }

      // Prefer vendorId-only route to avoid expenseId encoding pitfalls
      const url = `/api/expenses/vendors/${editingVendor.id}`;

      const response = await api.put(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const d1 = decodeURIComponentSafe(effectiveId);
      const norm = String(d1).replace(/%252F/gi, '%2F').replace(/%2F/gi, '/');
      const enc1 = encodeURIComponent(norm);
      const enc2 = encodeURIComponent(enc1);
      const encoded = import.meta.env.PROD ? enc2 : enc1;
      const vendorRes = await api.get(`/api/expenses/${encoded}/vendors`);
      setVendors(vendorRes.data);
      setShowEditModal(false);
    } catch (error) {
      console.error("Failed to update vendor:", error);
      alert("Failed to update vendor: " + (error.response?.data?.error || error.message));
    }
  };

  const handleEditDetails = () => {
    setEditDetails({
      expense_name: expense.expense_name || '',
      expense_category: expense.expense_category || '',
      department_id: expense.department_id || '',
      region_id: expense.region_id || '',
      expense_group: expense.expense_group || '',
      date: expense.date ? expense.date.split('T')[0] : '',
    });
    setShowEditDetailsModal(true);
  };

  const handleSaveDetails = async () => {
    try {
      const d1 = decodeURIComponentSafe(effectiveId);
      const norm = String(d1).replace(/%252F/gi, '%2F').replace(/%2F/gi, '/');
      const enc1 = encodeURIComponent(norm);
      const enc2 = encodeURIComponent(enc1);
      const encoded = import.meta.env.PROD ? enc2 : enc1;

      await api.patch(`/api/expenses/${encoded}`, editDetails);
      setExpense({ ...expense, ...editDetails });
      setShowEditDetailsModal(false);
    } catch (err) {
      console.error('Failed to update expense details:', err);
      alert('Update failed: ' + (err.response?.data?.error || err.message));
    }
  };

  if (loading) return <div className="p-6" style={{ color: 'var(--muted)' }}>Loading...</div>;
  if (!expense) return <div className="p-6" style={{ color: 'var(--muted)' }}>Expense not found</div>;

 
  return (
    <div className="p-6 w-full space-y-6" style={{ color: 'var(--text)' }}>
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text)' }}>Expense Report</h1>
          <p className="text font-semibold" style={{ color: 'var(--muted)' }}>{expense.expense_id}</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleExportPDF}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm shadow-sm transition border font-semibold"
            style={{ background: '#8c211e', color: '#ffffff', borderColor: '#721a18' }}
          >
            <FileDownIcon size={18} /> Export PDF
          </button>
        {expense.status === 'Draft' && (
          <button
            onClick={handleEditDetails}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm shadow-sm transition border"
            style={{ background: 'rgba(99,102,241,0.15)', color: '#4338ca', borderColor: 'rgba(99,102,241,0.35)' }}
          >
            <Pencil size={16} /> Edit Details
          </button>
        )}
        </div>
      </div>
 
      {/* PDF Content */}
      <div ref={pdfRef} className="space-y-4">
        {/* PDF Header (inside capture area) */}
        <div className="flex items-center justify-between border-b pb-3" style={{ borderColor: 'var(--border)' }}>
          <div>
            {/* <h1 className="text-2xl font-bold" style={{ color: 'var(--text)' }}>Expense Report</h1> */}
            {/* <div className="text-sm" style={{ color: 'var(--muted)' }}>Expense ID: <span style={{ color: 'var(--text)' }}>{expense.expense_id}</span></div> */}
            <div className="text-sm" style={{ color: 'var(--muted)' }}>Expense Title: <span style={{ color: 'var(--text)' }}>{expense.expense_name}</span></div>
          </div>
        </div>
 
        {/* Expense Fields */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-sm border-b pb-2" style={{ color: 'var(--text)', borderColor: 'var(--border)' }}>
            <TagIcon size={18} style={{ color: 'var(--muted)' }} />
            <span><strong>Category:</strong> {expense.expense_category}</span>
          </div>
          <div className="flex items-center gap-2 text-sm border-b pb-2" style={{ color: 'var(--text)', borderColor: 'var(--border)' }}>
            <BuildingIcon size={18} style={{ color: 'var(--muted)' }} />
            <span><strong>Department:</strong> {expense.department_id}</span>
          </div>
          <div className="flex items-center gap-2 text-sm border-b pb-2" style={{ color: 'var(--text)', borderColor: 'var(--border)' }}>
            <MapPinIcon size={18} style={{ color: 'var(--muted)' }} />
            <span><strong>Region:</strong> {expense.region_id}</span>
          </div>
          <div className="flex items-center gap-2 text-sm border-b pb-2" style={{ color: 'var(--text)', borderColor: 'var(--border)' }}>
            <LayersIcon size={18} style={{ color: 'var(--muted)' }} />
            <span><strong>Group:</strong> {expense.expense_group}</span>
          </div>
          <div className="flex items-center gap-2 text-sm border-b pb-2" style={{ color: 'var(--text)', borderColor: 'var(--border)' }}>
            <CalendarIcon size={18} style={{ color: 'var(--muted)' }} />
            <span><strong>Date:</strong> {new Date(expense.date).toLocaleDateString()}</span>
          </div>
          <div className="flex items-center gap-2 text-sm border-b pb-2" style={{ color: 'var(--text)', borderColor: 'var(--border)' }}>
            <UserIcon size={18} style={{ color: 'var(--muted)' }} />
            <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--text)' }}>
              <span className="font-bold" style={{ color: 'var(--text)' }}>Status:</span>
              {(() => {
                const s = derivedApproved ? 'Approved' : (expense?.status || '');
                const cls =
                  s === 'Pending Level 1' ? 'bg-yellow-100 text-yellow-800' :
                  s === 'Pending Level 2' ? 'bg-orange-100 text-orange-800' :
                  s === 'Pending Level 3' ? 'bg-blue-100 text-blue-800' :
                  s === 'Approved' ? 'bg-green-100 text-green-800' :
                  s === 'Rejected' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800';
                return (
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${cls}`}>
                    {s}
                  </span>
                );
              })()}
            </div>
          </div>
        </div>
 
        {/* Vendor Table */}
        <div className="pt-6">
          <h2 className="text-lg font-semibold">Vendor Details</h2>
          {vendors.length === 0 ? (
            <div className="p-4" style={{ color: 'var(--muted)' }}>No vendor details found.</div>
          ) : (
            <table className="min-w-full text-sm text-left mt-4" style={{ color: 'var(--text)' }}>
              <thead className="border-b" style={{ background: 'var(--panel)', borderColor: 'var(--border)', color: 'var(--muted)' }}>
                <tr>
                  <th className="p-3">Vendor Name</th>
                  <th className="p-3">Cost</th>
                  <th className="p-3">Date</th>
                  <th className="p-3">Payment Terms</th>
                  <th className="p-3">Description</th>
                </tr>
              </thead>
              <tbody>
                {vendors.map((v, idx) => (
                  <tr key={idx} className="border-t" style={{ borderColor: 'var(--border)' }}>
                    <td className="p-3">{v.vendor_name}</td>
                    <td className="p-3 font-medium">{formatCurrency(v.cost, v.currency || 'INR')}</td>
                    <td className="p-3">{new Date(v.date).toLocaleDateString()}</td>
                    <td className="p-3">{v.payment_terms}</td>
                    <td className="p-3">{v.description || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
 
      {/* Actions */}
      <div className="flex flex-col gap-12 mt-12"></div>
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold">Vendor Details</h2>
        <div className="flex gap-2">
          {expense.status === 'Draft' && (
            <button
              className="flex items-center gap-2 px-4 py-2 rounded-md transition-all"
              style={{ background: 'rgba(34,197,94,0.15)', color: '#16a34a', border: '1px solid rgba(34,197,94,0.35)', boxShadow: 'var(--shadow-sm)' }}
              onClick={handleSubmitForApproval}
            >
              <Send size={18} /> Submit for Approval
            </button>
          )}
          <Link
            to={`/expense-details/${encodeURIComponent(effectiveId)}`}
            className="flex items-center gap-2 px-4 py-2 rounded-md transition-all"
            style={{ background: 'rgba(239,68,68,0.15)', color: '#dc2626', border: '1px solid rgba(239,68,68,0.35)', boxShadow: 'var(--shadow-sm)' }}
          >
            <UserPlus size={18} /> + Add Vendor
          </Link>
        </div>
      </div>
 
      {/* Vendor cards with their line items below each vendor */}
      <div className="mt-18 space-y-6">
        {vendors.length === 0 ? (
          <div className="p-4" style={{ color: 'var(--muted)' }}>No vendor details found.</div>
        ) : (
          vendors.map((v) => {
            const items = lineItems.filter(li => Number(li.vendor_detail_id || 0) === Number(v.id));
            const vendorTotal = items.reduce((s, li) => s + ((Number(li.ordered_qty)||0) * (Number(li.unit_price)||0)), 0);
            return (
              <div key={v.id} className="overflow-x-auto shadow-sm rounded-lg border" style={{ background: 'var(--panel)', borderColor: 'var(--border)' }}>
                <table className="min-w-full text-sm text-left" style={{ color: 'var(--text)' }}>
                  <thead className="border-b" style={{ background: 'var(--panel)', borderColor: 'var(--border)', color: 'var(--muted)' }}>
                    <tr>
                      <th className="p-3">Vendor Name</th>
                      <th className="p-3">Cost</th>
                      <th className="p-3">Date</th>
                      <th className="p-3">Payment Terms</th>
                      <th className="p-3">Description</th>
                      <th className="p-3">Attachment</th>
                      <th className="p-3">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-t" style={{ borderColor: 'var(--border)' }}>
                      <td className="p-3 font-medium">{v.vendor_name}</td>
                      <td className="p-3 font-medium">{formatCurrency(v.cost, v.currency || 'INR')}</td>
                      <td className="p-3">{new Date(v.date).toLocaleDateString()}</td>
                      <td className="p-3">{v.payment_terms}</td>
                      <td className="p-3">{v.description}</td>
                      <td className="p-3">
                        {v.attachment_url ? (
                          <a href={v.attachment_url} target="_blank" rel="noopener noreferrer" className="hover:underline" style={{ color: 'var(--accent)' }}>
                            View Quote
                          </a>
                        ) : <span style={{ color: 'var(--muted)' }}>No file</span>}
                      </td>
                      <td className="p-3 space-x-2">
                        <button
                          className="btn btn-sm btn-outline-primary me-2"
                          onClick={() => handleEditClick(v)}
                          title="Edit"
                        >
                          <i className="bi bi-pencil-square"></i>
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDeleteVendor(v.id)}
                          title="Delete"
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
                {/* Items for this vendor */}
                <div className="px-3 pb-3">
                  {items.length === 0 ? (
                    <div className="p-3 text-sm" style={{ color: 'var(--muted)' }}>No line items for this vendor.</div>
                  ) : (
                    <table className="min-w-full text-sm text-left mt-2">
                      <thead className="border-b" style={{ background: 'var(--panel)', borderColor: 'var(--border)', color: 'var(--muted)' }}>
                        <tr>
                          <th className="p-2">Type</th>
                          <th className="p-2">Item</th>
                          <th className="p-2">UOM</th>
                          <th className="p-2 text-right">Qty</th>
                          <th className="p-2 text-right">Unit Price</th>
                          <th className="p-2 text-right">Line Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((li, idx) => {
                          const name = li.type === 'Inventory' ? `${li.item_name || ''}${li.item_sku ? ` (${li.item_sku})` : ''}` : (li.free_text_name || '');
                          const qty = Number(li.ordered_qty) || 0;
                          const price = Number(li.unit_price) || 0;
                          const total = qty * price;
                          return (
                            <tr key={idx} className="border-t" style={{ borderColor: 'var(--border)' }}>
                              <td className="p-2">{li.type}</td>
                              <td className="p-2">{name}</td>
                              <td className="p-2">{li.uom}</td>
                              <td className="p-2 text-right">{qty}</td>
                              <td className="p-2 text-right">{formatCurrency(price, expense.currency || 'INR')}</td>
                              <td className="p-2 text-right">{formatCurrency(total, expense.currency || 'INR')}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td className="p-2 font-semibold" colSpan={5} style={{ textAlign: 'right' }}>Subtotal</td>
                          <td className="p-2 font-semibold text-right">{formatCurrency(vendorTotal, expense.currency || 'INR')}</td>
                        </tr>
                      </tfoot>
                    </table>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
      <button
        onClick={() => navigate('/expenses')}
        className="flex items-center gap-2 px-4 py-2 rounded-md transition-all border"
        style={{ background: 'rgba(99,102,241,0.18)', color: '#3730a3', borderColor: 'rgba(99,102,241,0.35)', boxShadow: 'var(--shadow-sm)' }}
      >
        <ArrowLeft size={18} />
        Back to Expense List
      </button>
 
  {/* === EDIT DETAILS MODAL === */}
      {showEditDetailsModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div className="w-full" style={{ maxWidth: '680px', maxHeight: '90vh', overflowY: 'auto', background: 'var(--bg)', borderRadius: '1rem', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>

            {/* Modal Header */}
            <div className="p-6 border-b" style={{ borderColor: 'var(--border)' }}>
              <div className="flex items-center gap-2">
                <BsFileEarmarkText className="text-xl" style={{ color: 'var(--accent)' }} />
                <h2 className="text-xl font-bold" style={{ color: 'var(--text)' }}>Edit Expense Details</h2>
              </div>
              <p className="text-sm mt-1" style={{ color: 'var(--muted)' }}>Update the details for this draft expense</p>
            </div>

            {/* Form Body */}
            <div className="p-6 space-y-5">

              {/* Expense Title */}
              <div>
                <label className="flex items-center text-sm font-medium mb-1" style={{ color: 'var(--text)' }}>
                  <BsFileEarmarkText className="mr-2" /> Expense Title <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                  type="text"
                  value={editDetails.expense_name}
                  onChange={(e) => setEditDetails({ ...editDetails, expense_name: e.target.value })}
                  placeholder="Enter expense title"
                  className="w-full py-2 px-3 rounded-md text-sm"
                  style={{ background: 'var(--panel)', color: 'var(--text)', border: '1px solid var(--border)' }}
                />
              </div>

              {/* Department + Region */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="flex items-center text-sm font-medium mb-1" style={{ color: 'var(--text)' }}>
                    <BsBuilding className="mr-2" /> Department <span className="text-red-500 ml-1">*</span>
                  </label>
                  <select
                    value={editDetails.department_id}
                    onChange={(e) => setEditDetails({ ...editDetails, department_id: e.target.value, expense_category: '' })}
                    className="w-full py-2 px-3 rounded-md text-sm"
                    style={{ background: 'var(--panel)', color: 'var(--text)', border: '1px solid var(--border)' }}
                  >
                    <option value="" disabled>Select Department</option>
                    {departments.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
                <div>
                  <label className="flex items-center text-sm font-medium mb-1" style={{ color: 'var(--text)' }}>
                    <BsGeoAlt className="mr-2" /> Region <span className="text-red-500 ml-1">*</span>
                  </label>
                  <select
                    value={editDetails.region_id}
                    onChange={(e) => setEditDetails({ ...editDetails, region_id: e.target.value })}
                    className="w-full py-2 px-3 rounded-md text-sm"
                    style={{ background: 'var(--panel)', color: 'var(--text)', border: '1px solid var(--border)' }}
                  >
                    <option value="" disabled>Select Region</option>
                    {regions.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
              </div>

              {/* Group + Category */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="flex items-center text-sm font-medium mb-1" style={{ color: 'var(--text)' }}>
                    <BsBoxSeam className="mr-2" /> Expense Group <span className="text-red-500 ml-1">*</span>
                  </label>
                  <select
                    value={editDetails.expense_group}
                    onChange={(e) => setEditDetails({ ...editDetails, expense_group: e.target.value, expense_category: '' })}
                    disabled={!editDetails.department_id}
                    className="w-full py-2 px-3 rounded-md text-sm"
                    style={{ background: 'var(--panel)', color: 'var(--text)', border: '1px solid var(--border)', opacity: !editDetails.department_id ? 0.5 : 1 }}
                  >
                    <option value="" disabled>Select Group</option>
                    {groups.map(g => <option key={g} value={g}>{g}</option>)}
                  </select>
                </div>
                <div>
                  <label className="flex items-center text-sm font-medium mb-1" style={{ color: 'var(--text)' }}>
                    <BsFileEarmarkText className="mr-2" /> Expense Category <span className="text-red-500 ml-1">*</span>
                  </label>
                  <select
                    value={editDetails.expense_category}
                    onChange={(e) => setEditDetails({ ...editDetails, expense_category: e.target.value })}
                    disabled={!editDetails.department_id || !editDetails.expense_group}
                    className="w-full py-2 px-3 rounded-md text-sm"
                    style={{ background: 'var(--panel)', color: 'var(--text)', border: '1px solid var(--border)', opacity: (!editDetails.department_id || !editDetails.expense_group) ? 0.5 : 1 }}
                  >
                    <option value="" disabled>Select Category</option>
                    {(categoryOptions[editDetails.department_id]?.[editDetails.expense_group] || []).map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Date */}
              <div>
                <label className="flex items-center text-sm font-medium mb-1" style={{ color: 'var(--text)' }}>
                  <BsCalendarDate className="mr-2" /> Date <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                  type="date"
                  value={editDetails.date}
                  onChange={(e) => setEditDetails({ ...editDetails, date: e.target.value })}
                  className="w-full py-2 px-3 rounded-md text-sm"
                  style={{ background: 'var(--panel)', color: 'var(--text)', border: '1px solid var(--border)' }}
                />
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 px-6 pb-6">
              <button
                onClick={() => setShowEditDetailsModal(false)}
                className="py-2 px-5 rounded-md text-sm font-medium border"
                style={{ background: 'var(--panel)', color: 'var(--text)', borderColor: 'var(--border)' }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveDetails}
                className="py-2 px-5 rounded-md text-sm font-semibold border"
                style={{ background: 'var(--primary)', color: '#fff', borderColor: 'var(--primary-700)' }}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

  {/* === EDIT MODAL === */}
      {showEditModal && editingVendor && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Vendor</h5>
                <button type="button" className="btn-close" onClick={() => setShowEditModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Vendor Name</label>
                  <input type="text" className="form-control" name="vendor_name" value={editingVendor.vendor_name} onChange={handleEditChange} />
                </div>
                <div className="mb-3">
                  <label className="form-label">Cost</label>
                  <input type="number" className="form-control" name="cost" value={editingVendor.cost} onChange={handleEditChange} />
                </div>
                <div className="mb-3">
                  <label className="form-label">Payment Terms</label>
                  <input type="text" className="form-control" name="payment_terms" value={editingVendor.payment_terms} onChange={handleEditChange} />
                </div>
                <div className="mb-3">
                  <label className="form-label">Description</label>
                  <input type="text" className="form-control" name="description" value={editingVendor.description} onChange={handleEditChange} />
                </div>
                <div className="mb-3">
                  <label className="form-label">Quote (.pdf/.jpg)</label>
                  <input type="file" className="form-control" accept=".pdf,image/*" onChange={(e) => setEditingVendor({ ...editingVendor, newFile: e.target.files[0] })}/>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowEditModal(false)}>Cancel</button>
                <button className="btn btn-primary" onClick={handleEditSave}>Save Changes</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
 
 
 
export default ExpenseReport;